import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Menu } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { PopoverOrigin } from '@mui/material/Popover';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import HideSourceIcon from '@mui/icons-material/HideSourceSharp';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { DateTime, Duration } from 'luxon';
import { ProgressButton, SimpleDialog } from 'modules/common/components';
import { useDialogState, DialogCancelProps } from 'modules/common/hooks';
import { TimeSignalClassification } from 'graphql/types';
import { useTrackRenderCounts } from 'modules/common/components/Render';
import { useFormErrorDialogContext } from 'modules/notes/common';

import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { useTimerContext } from '../Timer';
import { NoteData } from '../../noteSchema';
import { BulkSuggestionAcceptAllActions } from '../../../../timesheet-labels/BulkSuggestionAcceptAllActions';
import NoteActionMenutItem from './NoteActionMenutItem';
export const menuProps: {
  anchorOrigin?: PopoverOrigin | undefined;
  transformOrigin?: PopoverOrigin | undefined;
} = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center'
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'center'
  }
};

export const useMenuStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: theme.shadow?.paper
  }
}));

export const menuItems = [
  {
    icon: <ThumbUpIcon fontSize="small" />,
    title: 'timeSignalActions.accepted',
    classification: TimeSignalClassification.Accepted
  },
  {
    icon: <HideSourceIcon fontSize="small" />,
    title: 'timeSignalActions.private',
    classification: TimeSignalClassification.Private
  },
  {
    icon: <InboxIcon fontSize="small" />,
    title: 'timeSignalActions.inbox',
    classification: TimeSignalClassification.Unclassified
  }
];

export const messages = {
  addStopTimerDialog: [
    <FormattedMessage
      key="note.stopTimerDialog.addMessage"
      id="note.stopTimerDialog.addMessage"
    />,
    <FormattedMessage
      key="note.stopTimerDialog.message2"
      id="note.stopTimerDialog.message2"
    />
  ],
  updateStopTimerDialog: [
    <FormattedMessage
      key="note.stopTimerDialog.updateMessage"
      id="note.stopTimerDialog.updateMessage"
    />,
    <FormattedMessage
      key="note.stopTimerDialog.message2"
      id="note.stopTimerDialog.message2"
    />
  ],
  cancelDialog: [
    <FormattedMessage
      key="note.cancelDialog.message1"
      id="note.cancelDialog.message1"
    />,
    <FormattedMessage
      key="note.cancelDialog.message2"
      id="note.cancelDialog.message2"
    />
  ],
  stopTimerOk: <FormattedMessage id="note.stopTimerDialog.yes" />,
  stopTimerCancel: <FormattedMessage id="note.stopTimerDialog.cancel" />,
  cancelDialogYes: <FormattedMessage id="note.cancelDialog.yes" />,
  cancelDialogNo: <FormattedMessage id="note.cancelDialog.no" />
};
const useStyles = makeStyles((theme) => ({
  addNote: {
    fontWeight: 600
  },
  cancel: {
    marginRight: theme.spacing(1)
  },
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: ({ isSuggestion }: { isSuggestion: boolean }) =>
      isSuggestion ? 'space-between' : 'flex-end',
    padding: theme.spacing(2, 0)
  },
  action: {
    display: 'flex',
    justifyContent: ({ isSuggestion }: { isSuggestion: boolean }) =>
      isSuggestion ? 'space-between' : 'flex-end'
  },
  accept: {
    display: 'flex'
  }
}));

type NoteFooterProps = DialogCancelProps & {
  handleSubmit: UseFormReturn['handleSubmit'];
  onSubmitNote: (note: NoteData) => Promise<void>;
  loading: boolean;
  isValidForm: boolean;
  saveTitle: string;
  onClassificationChange(classification: TimeSignalClassification | null): void;
  mode?: 'add' | 'edit';
  classification?: TimeSignalClassification | null;
  onSaveNoteWithTimerRunning(
    duration: Duration | null,
    showStartEndTime?: boolean
  ): void;
  showStartEndTime: boolean;
  customLabels: string;
  timesheetLabels: string;
};

const useSaveAction = ({
  handleSubmit,
  onSubmitNote,
  handleDuration,
  showStartEndTime,
  isValidForm
}: {
  handleSubmit: UseFormReturn['handleSubmit'];
  onSubmitNote: (note: NoteData) => Promise<void>;
  handleDuration(duration: Duration | null, showStartEndTime?: boolean): void;
  showStartEndTime: boolean;
  isValidForm: boolean;
}) => {
  const { running, stopTimer, startTime, setStartTime } = useTimerContext();
  const [disabled, setDisabled] = useState(false);

  useMemo(() => {
    if (disabled) {
      setDisabled(isValidForm);
    }
  }, [disabled, isValidForm]);

  const {
    open: openSave,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog
  } = useDialogState();

  const saveNote = useCallback(async () => {
    if (startTime) {
      handleDuration(DateTime.local().diff(startTime), showStartEndTime);
    }
    stopTimer();
    setStartTime(null);
    setDisabled(true);
    closeSaveDialog();
    await handleSubmit(onSubmitNote)();
  }, [
    closeSaveDialog,
    handleDuration,
    handleSubmit,
    onSubmitNote,
    setStartTime,
    showStartEndTime,
    startTime,
    stopTimer
  ]);

  const onSaveClick = useCallback(async () => {
    if (running) {
      openSaveDialog();
      return;
    }
    setDisabled(true);
    await handleSubmit(onSubmitNote)();
  }, [running, handleSubmit, onSubmitNote, openSaveDialog]);

  return useMemo(
    () => ({
      openSave,
      onSaveClick,
      saveNote,
      closeSaveDialog,
      disabled
    }),
    [closeSaveDialog, disabled, onSaveClick, openSave, saveNote]
  );
};

const NoteFooterFC: React.FC<NoteFooterProps> = ({
  handleSubmit,
  onSubmitNote,
  loading,
  isValidForm,
  onClassificationChange,
  openCancel,
  onNoteDiscard,
  discardNote,
  closeCancelDialog,
  mode = 'add',
  classification,
  onSaveNoteWithTimerRunning: handleDuration,
  showStartEndTime,
  customLabels,
  timesheetLabels
}: NoteFooterProps) => {
  useTrackRenderCounts('note-dialog-footer');
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const classes = useStyles({ isSuggestion: isSmartSuggestionEnabled });

  const { errorContext } = useFormErrorDialogContext();
  const {
    openSave,
    onSaveClick,
    closeSaveDialog,
    saveNote,
    disabled
  } = useSaveAction({
    handleSubmit,
    onSubmitNote,
    handleDuration,
    showStartEndTime,
    isValidForm
  });

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl]
  );
  const onCloseMenu = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const onSave = useCallback(
    (classification: TimeSignalClassification) => {
      onClassificationChange(classification);
      onCloseMenu();
      onSaveClick();
    },
    [onClassificationChange, onSaveClick, onCloseMenu]
  );
  const menuClasses = useMenuStyles();

  return (
    <>
      <Box className={classes.container}>
        {isSmartSuggestionEnabled && (
          <div>
            <BulkSuggestionAcceptAllActions
              customLabels={customLabels}
              timesheetLabels={timesheetLabels}
            />
          </div>
        )}
        <div className={classes.action}>
          <Button
            data-testid="add-note-cancel-btn"
            variant="text"
            color="secondary"
            className={classes.cancel}
            onClick={onNoteDiscard}
          >
            <FormattedMessage id="note.cancel" />
          </Button>
          <ProgressButton
            data-testid="add-note-submit-btn"
            variant="contained"
            color="primary"
            disabled={
              !isValidForm ||
              disabled ||
              errorContext.noteExceeded ||
              errorContext.commentExceeded
            }
            loading={loading}
            className={classes.addNote}
            onClick={handleClick}
          >
            <FormattedMessage id="note.saveNote" />
          </ProgressButton>
          <Menu
            elevation={0}
            data-testid="note-actions-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onCloseMenu}
            classes={menuClasses}
            {...menuProps}
          >
            {menuItems.map(
              ({ title, icon, classification: itemClassification }) => (
                <NoteActionMenutItem
                  key={title}
                  title={title}
                  icon={icon}
                  classification={itemClassification}
                  selectedClassification={classification}
                  onSave={onSave}
                  disabled={!isValidForm || disabled}
                />
              )
            )}
          </Menu>
        </div>
      </Box>
      {openSave && (
        <SimpleDialog
          open={openSave}
          onClose={closeSaveDialog}
          onPrimaryAction={closeSaveDialog}
          onSecondaryAction={saveNote}
          title={<FormattedMessage id="note.stopTimerDialog.title" />}
          messages={
            mode === 'add'
              ? messages.addStopTimerDialog
              : messages.updateStopTimerDialog
          }
          primaryActionButtonTitle={messages.stopTimerCancel}
          secondaryActionButtonTitle={messages.stopTimerOk}
        />
      )}
      {openCancel && (
        <SimpleDialog
          open={openCancel}
          onClose={closeCancelDialog}
          onPrimaryAction={closeCancelDialog}
          onSecondaryAction={discardNote}
          title={<FormattedMessage id="note.cancelDialog.title" />}
          messages={messages.cancelDialog}
          primaryActionButtonTitle={messages.cancelDialogNo}
          secondaryActionButtonTitle={messages.cancelDialogYes}
        />
      )}
    </>
  );
};

export const NoteFooter = React.memo(NoteFooterFC);
